/*------------------------------------------------------------------------------
   About      : Pure catalog lookups for the NBI Insights screen
                (epic DA-697, II-FE-1)

                Every function here is pure and takes the catalog explicitly, so
                each can be tested without rendering anything — the shape the
                survey mapper builder uses, and the reason that module is the
                best-tested code in the repo.

                Two invariants these exist to enforce:

                1. **Never render a raw enum name.** `SELF_NBC_COMPARE` is not a
                   label. Every user-visible string resolves through
                   `displayName`.
                2. **Tokens are computed, never stored.** The insertable token is
                   `${CODE}` — dollar-brace, uppercase, the exact pingpong
                   `DerivedQuantity` constant. A display name must never reach
                   stored template text, and a `{{value}}`-style token is never
                   substituted by pingpong and ships literally to end users.

   Copyright (c) 2026 Bidgely. All rights reserved.
------------------------------------------------------------------------------*/
import { CriteriaGrammar,
         DerivedQuantityVariable,
         InsightApplianceOption,
         InsightCatalog,
         InsightGroupDef,
         InsightTypeDef }        from '../types/insightTypes'

/*------------------------------------------------------------------------------
   Display names
------------------------------------------------------------------------------*/

export type InsightCatalogKind = 'derivedQuantity' | 'type' | 'group' | 'applianceCategory'

/**
 * Resolves a stored code to the text a user may see.
 *
 * Falls back to the code itself when the catalog has no row. That is deliberate
 * and it is the least-bad option: a record can legitimately carry a deprecated
 * or newly-added code the cached catalog has not caught up with, and showing
 * `NC-71-N`'s raw type beats showing an empty cell that reads as "unset". The
 * fallback is the signal that the catalog needs a refresh, so it must stay
 * visible rather than being silently blanked.
 */
export function displayNameFor(
  catalog : InsightCatalog | undefined,
  kind    : InsightCatalogKind,
  code    : string | null | undefined,
): string {
  if (!code) return ''
  if (!catalog) return code

  const row = (() => {
    switch (kind) {
      case 'derivedQuantity'   : return catalog.derivedQuantities?.find((d) => d.code === code)
      case 'type'              : return catalog.types?.find((t) => t.code === code)
      case 'group'             : return catalog.groups?.find((g) => g.code === code)
      case 'applianceCategory' : return catalog.applianceCategories?.find((c) => c.code === code)
      default                  : return undefined
    }
  })()

  return row?.displayName || code
}

/** Appliances are keyed by numeric id, not by a string code. */
export function applianceNameFor(
  catalog     : InsightCatalog | undefined,
  applianceId : number | null | undefined,
): string {
  if (applianceId === null || applianceId === undefined) return ''
  const row = catalog?.appliances?.find((a) => a.applianceId === applianceId)
  return row?.displayName || String(applianceId)
}

/*------------------------------------------------------------------------------
   Tokens
------------------------------------------------------------------------------*/

/** `${CODE}`. Computed, never read from a field. */
export function tokenFor(variable: Pick<DerivedQuantityVariable, 'code'>): string {
  return `\${${variable.code}}`
}

/**
 * `${CODE_FORMAT}` for the two date variables.
 *
 * The suffix is **mandatory, not optional**, and the three outcomes in pingpong
 * are not equally visible:
 *
 *   ${BILL_END_MONTH_NAME}    → renders correctly
 *   ${BILL_END_NOT_A_FORMAT}  → `DateFormat.valueOf` throws, IS caught, silently
 *                               falls back to MONTH_IN_WORDS_DAY_SPACE. Wrong
 *                               date shipped, ERROR log only.
 *   ${BILL_END}               → the split returns a 1-element array and `[1]`
 *                               throws ArrayIndexOutOfBoundsException, which the
 *                               `catch (IllegalArgumentException)` does NOT
 *                               catch. It propagates out of insight computation.
 *
 * Across 3,721 uses of these two variables in pingpong's seed data, not one is
 * bare. So this throws rather than returning a bare token: a caller that has not
 * collected a format has a bug, and failing at the call site is far cheaper than
 * shipping either failure mode above.
 */
export function tokenForWithDateFormat(
  variable   : Pick<DerivedQuantityVariable, 'code' | 'dateFormatSuffix' | 'supportedDateFormats'>,
  dateFormat?: string | null,
): string {
  if (!variable.dateFormatSuffix) return tokenFor(variable)

  if (!dateFormat) {
    throw new Error(
      `${variable.code} requires a date format suffix; a bare \${${variable.code}} ` +
      `throws out of pingpong's insight computation`,
    )
  }
  if (variable.supportedDateFormats?.length && !variable.supportedDateFormats.includes(dateFormat)) {
    throw new Error(
      `"${dateFormat}" is not a supported format for ${variable.code} ` +
      `(allowed: ${variable.supportedDateFormats.join(', ')})`,
    )
  }
  return `\${${variable.code}_${dateFormat}}`
}

/** Matches the only substitution pingpong performs: `${UPPER_SNAKE}`. */
export const INSIGHT_TOKEN_PATTERN = /\$\{([A-Z][A-Z0-9_]*)\}/g

/** Every `${…}` token in template text, in order of appearance, deduped. */
export function extractTokens(templateString: string | null | undefined): string[] {
  if (!templateString) return []
  const seen : string[] = []
  for (const match of templateString.matchAll(INSIGHT_TOKEN_PATTERN)) {
    if (!seen.includes(match[1])) seen.push(match[1])
  }
  return seen
}

/**
 * Splits a token into its variable and optional date-format suffix, resolving
 * against the catalog rather than by guessing where the underscore falls —
 * `USAGE_DELTA_PERCENTAGE` has underscores and no suffix, so a positional split
 * would mangle it.
 *
 * Returns `known: false` for a token no catalog row explains, which is what the
 * preview and validation surfaces render as an error rather than as blank.
 */
export interface ResolvedToken {
  raw         : string
  code        : string
  dateFormat ?: string
  known       : boolean
  /** True when the row needs a suffix and the token has none — the throwing case. */
  bareDateToken : boolean
}

export function resolveToken(
  catalog : InsightCatalog | undefined,
  raw     : string,
): ResolvedToken {
  const rows = catalog?.derivedQuantities ?? []

  const exact = rows.find((r) => r.code === raw)
  if (exact) {
    return { raw, code : exact.code, known : true, bareDateToken : exact.dateFormatSuffix }
  }

  // Longest-prefix match so a code containing an underscore is not split early.
  const withSuffix = rows
    .filter((r) => r.dateFormatSuffix && raw.startsWith(`${r.code}_`))
    .sort((a, b) => b.code.length - a.code.length)[0]

  if (withSuffix) {
    return {
      raw,
      code          : withSuffix.code,
      dateFormat    : raw.slice(withSuffix.code.length + 1),
      known         : true,
      bareDateToken : false,
    }
  }

  return { raw, code : raw, known : false, bareDateToken : false }
}

/**
 * Criteria variables that the template text never mentions.
 *
 * A `templateCriteria` entry gates whether the insight fires, but pingpong caps
 * and renders from the values the template text actually references. A variable
 * that is constrained but never mentioned is computed for a sentence that does
 * not use it, so its capping is not applied to anything the reader sees — which
 * is why the author has to put every criteria variable in the text.
 *
 * Date variables match on their base code: `BILL_END` in the criteria is
 * satisfied by `${BILL_END_MONTH_NAME}` in the text, since both resolve to the
 * same DerivedQuantity.
 */
export function criteriaVariablesMissingFromText(
  templateCriteria : Record<string, string> | null | undefined,
  templateString   : string | null | undefined,
  catalog          : InsightCatalog | undefined,
): string[] {
  const codes = Object.keys(templateCriteria ?? {})
  if (!codes.length) return []

  const referenced = new Set(
    extractTokens(templateString).map((raw) => resolveToken(catalog, raw).code),
  )
  return codes.filter((code) => !referenced.has(code))
}

/*------------------------------------------------------------------------------
   Definition-field dependencies
------------------------------------------------------------------------------*/

/** The catalog row for a code, or undefined when nothing matches. */
export function variableFor(
  catalog : InsightCatalog | undefined,
  code    : string | null | undefined,
): DerivedQuantityVariable | undefined {
  if (!code) return undefined
  return catalog?.derivedQuantities?.find((r) => r.code === code)
}

/** One token that reads a record field the record has not set. */
export interface DefinitionFieldGap {
  /** The DerivedQuantity code, e.g. `PICK_UNIT_TYPE`. */
  code  : string
  /** The insight field it reads, e.g. `pickUnitType`. */
  field : string
}

/**
 * Tokens and criteria keys whose value comes from a record field that is unset.
 *
 * This is the local mirror of quill's `IIValidationUtils.checkDefinitionField`,
 * and it exists because the server cannot answer it for us: the stateless
 * `validate-template-text` endpoint is handed the text and the type but never
 * the record, so it reports the dependency and skips the check. Without this,
 * an author sees a green VARIABLES tile and an enabled Save, then the save is
 * rejected by `createInsight` / `updateAll` for a reason nothing on screen
 * predicted.
 *
 * Not a duplicate of the resolvability matrix, which stays server-side. That
 * asks "does this insight's TYPE resolve this variable?"; this asks "did the
 * author fill in the field it reads?" — a question only the form can answer.
 *
 * Note `0` is a legitimate count, so this is an unset check rather than a
 * falsiness check.
 */
export function definitionFieldGaps(
  insight : {
    templateString   ?: string | null
    templateCriteria ?: Record<string, string> | null
  } & Record<string, unknown>,
  catalog : InsightCatalog | undefined,
): DefinitionFieldGap[] {

  if (!catalog) return []

  // Criteria keys matter as much as text tokens — arguably more. An unset value
  // there NPEs inside pingpong's InsightValidator, which is caught and silently
  // yields zero insights rather than a visible broken token.
  const codes = new Set<string>([
    ...extractTokens(insight.templateString).map((raw) => resolveToken(catalog, raw).code),
    ...Object.keys(insight.templateCriteria ?? {}),
  ])

  const gaps : DefinitionFieldGap[] = []

  for (const code of codes) {
    const field = variableFor(catalog, code)?.requiresDefinitionField
    if (!field) continue

    const value = insight[field]
    if (value === undefined || value === null || value === '') {
      gaps.push({ code, field })
    }
  }

  return gaps
}

/*------------------------------------------------------------------------------
   Option lists
------------------------------------------------------------------------------*/

/** Ascending `sortOrder`, ties broken by display name for a stable order. */
function bySortOrder(a: DerivedQuantityVariable, b: DerivedQuantityVariable): number {
  return (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.displayName.localeCompare(b.displayName)
}

export interface DerivedQuantityFilter {
  /** Restrict to variables that resolve for this insight type. */
  insightType ?: string | null
  usage        : 'templateText' | 'templateCriteria'
  /** Codes already on the record — kept even when deprecated, so an existing
   *  value never vanishes from its own editor. */
  keepCodes   ?: string[]
}

/**
 * The chip / dropdown list for a given surface.
 *
 * Deprecated rows are excluded from *offers* but retained when already in use:
 * dropping a code a record still carries would silently blank a stored value on
 * the next save.
 */
export function derivedQuantitiesFor(
  catalog : InsightCatalog | undefined,
  filter  : DerivedQuantityFilter,
): DerivedQuantityVariable[] {
  const rows = catalog?.derivedQuantities ?? []
  const keep = new Set(filter.keepCodes ?? [])

  return rows
    .filter((row) => {
      if (keep.has(row.code)) return true
      if (row.deprecated) return false

      const usable = filter.usage === 'templateText'
        ? row.usableInTemplateText
        : row.usableInTemplateCriteria
      if (!usable) return false

      // `null` is the contract's "not gated by insight type" — the expected value
      // for every DEFINITION, DURATION and HYBRID row, which is most of them.
      // Treating it as unrestricted is required, not incidental: gating
      // ${APPLIANCE} or ${BILL_END} by type withholds variables the author is
      // entitled to use.
      if (filter.insightType && Array.isArray(row.allowedInsightTypes) && row.allowedInsightTypes.length) {
        return row.allowedInsightTypes.includes(filter.insightType)
      }
      return true
    })
    .sort(bySortOrder)
}

export function typeDefFor(
  catalog : InsightCatalog | undefined,
  code    : string | null | undefined,
): InsightTypeDef | undefined {
  if (!code) return undefined
  return catalog?.types?.find((t) => t.code === code)
}

export function groupDefFor(
  catalog : InsightCatalog | undefined,
  code    : string | null | undefined,
): InsightGroupDef | undefined {
  if (!code) return undefined
  return catalog?.groups?.find((g) => g.code === code)
}

export function applianceOptions(catalog: InsightCatalog | undefined): InsightApplianceOption[] {
  // Defensive `?? ''`: a catalog that skipped `normalizeInsightCatalog` — or a
  // future field rename upstream — must degrade to an unsorted-ish list, not
  // throw inside Array.sort and take the whole form down.
  return [...(catalog?.appliances ?? [])]
    .sort((a, b) => (a.displayName ?? '').localeCompare(b.displayName ?? ''))
}

/*------------------------------------------------------------------------------
   Catalog normalisation

   quill's `ii-catalog-service.buildCatalog` spreads the `appliance` and
   `appliance-category` Strapi rows into the payload **unreshaped** — the other
   catalog sections are mapped to `{ code, displayName }` but these two are not.
   So what actually arrives is

     appliances          : { applianceId, name, categoryId, subCategoryId, fuelType }
     applianceCategories : { categoryId, shortCode, name, trueDisagg, ... }

   not the `{ displayName, category }` / `{ code, displayName }` this feature's
   types describe. The contract doc doesn't settle it either — its sample has
   both arrays empty.

   Rather than teach every call site two shapes, normalise once here, at the
   boundary. Accepts either spelling and is idempotent, so it keeps working if
   quill later reshapes to match the contract (and so the already-normalised
   fixtures stay valid).
------------------------------------------------------------------------------*/

/** The raw appliance row, before normalisation. */
interface RawApplianceRow {
  applianceId ?: number | string | null
  name        ?: string | null
  displayName ?: string | null
  categoryId  ?: string | null
  category    ?: string | null
}

/** The raw appliance-category row, before normalisation. */
interface RawApplianceCategoryRow {
  categoryId  ?: string | null
  code        ?: string | null
  name        ?: string | null
  displayName ?: string | null
}

export function normalizeInsightCatalog(raw: InsightCatalog): InsightCatalog {
  const rawAppliances  = (raw?.appliances ?? []) as RawApplianceRow[]
  const rawCategories  = (raw?.applianceCategories ?? []) as RawApplianceCategoryRow[]

  return {
    ...raw,

    appliances : rawAppliances
      // `applianceId` is how every insight references an appliance; a row
      // without one can never match, so it would only be a blank dropdown entry.
      .filter((row) => row?.applianceId !== null && row?.applianceId !== undefined)
      .map((row) => ({
        applianceId : Number(row.applianceId),
        // Never blank: falling back to the id keeps the option selectable and
        // makes a catalog gap visible rather than showing an empty menu item.
        displayName : row.displayName || row.name || String(row.applianceId),
        category    : row.category ?? row.categoryId ?? null,
      })),

    applianceCategories : rawCategories
      .map((row) => ({
        code        : row.code ?? row.categoryId ?? '',
        displayName : row.displayName || row.name || row.code || row.categoryId || '',
      }))
      // An empty code matches nothing and collides with "unset" in the filter
      // drawer, so drop the row instead of offering it.
      .filter((row) => !!row.code),
  }
}

/**
 * Whether a group can have a `code` derived for it at all.
 *
 * `SEASON_COMPARISON` and `SOLAR_GENERATION` carry `shortCode: null` because no
 * pingpong definition establishes a prefix for them, and quill's `buildCode`
 * refuses rather than falling back to the type short code — a wrong prefix
 * writes a wrong Cassandra clustering key. Those need `codeOverride`, which is
 * FULL_ACCESS-only, so the form should say so up front instead of letting the
 * user fill everything in and then eat a server rejection.
 */
export function groupSupportsDerivation(
  catalog : InsightCatalog | undefined,
  code    : string | null | undefined,
): boolean {
  const group = groupDefFor(catalog, code)
  return !!group?.shortCode
}

/*------------------------------------------------------------------------------
   Criteria grammar
------------------------------------------------------------------------------*/

/**
 * Compiles `criteriaGrammar.pattern` into a usable RegExp.
 *
 * ⚠️ The pattern is used **verbatim**. `compoundToken` (`%s`) is NOT a
 * placeholder to substitute out — it is a literal part of the stored
 * expression, and `%` and `s` are literal characters in a regex, so the pattern
 * already matches it as written.
 *
 * quill is explicit about this (`ii-interfaces.ts`): "The `%s` token is
 * substituted with the runtime value first, which is what makes compound
 * predicates work: `\">10 && %s<60\"` with a value of 28.9 becomes …". So the
 * second bound of a compound expression carries `%s` where its left-hand value
 * goes, and `compoundToken` is in the catalog so that **this client can emit
 * it** — see `formatCriteriaExpression`.
 *
 * Stripping it would invert the validation: `">10 && <60"` would pass here and
 * then be rejected by quill, while the correct `">10 && %s<60"` would be
 * refused locally.
 */
export function compileCriteriaPattern(grammar: CriteriaGrammar | undefined): RegExp | undefined {
  if (!grammar?.pattern) return undefined
  try {
    return new RegExp(grammar.pattern)
  } catch {
    // A malformed pattern must not take the builder down; the caller treats
    // `undefined` as "cannot validate locally" and lets the server decide.
    return undefined
  }
}

/** Local pre-flight only. quill (DA-2368) is the enforcement layer. */
export function isValidCriteriaExpression(
  grammar    : CriteriaGrammar | undefined,
  expression : string,
): boolean {
  const pattern = compileCriteriaPattern(grammar)
  if (!pattern) return true
  return pattern.test(expression.trim())
}
